import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faChevronLeft,
	faChevronRight,
	faHome,
	faUser,
	faRedo,
	faChartBar,
	faEnvelope,
} from "@fortawesome/free-solid-svg-icons"
import "./index.scss"

/**
 * PWA Navigation Bar Component
 *
 * Provides native app-like navigation controls when running in standalone mode.
 * Only renders when the app is installed and running as a PWA.
 *
 * Button order: Back, Forward, Refresh, Home (centered), Messages, Dashboard, Account
 */
const PWANavBar = ({ unreadCount }) => {
	const history = useHistory()
	const location = useLocation()
	const [isStandalone, setIsStandalone] = useState(false)
	const [canGoBack, setCanGoBack] = useState(false)

	useEffect(() => {
		// Check if running in standalone mode (installed PWA)
		const checkStandalone = () => {
			const standalone =
				window.matchMedia("(display-mode: standalone)").matches ||
				window.navigator.standalone === true ||
				document.referrer.includes("android-app://")
			setIsStandalone(standalone)

			// Add body class for CSS adjustments
			if (standalone) {
				document.body.classList.add("pwa-standalone")
			} else {
				document.body.classList.remove("pwa-standalone")
			}
		}

		checkStandalone()

		// Listen for display mode changes
		const mediaQuery = window.matchMedia("(display-mode: standalone)")
		mediaQuery.addEventListener("change", checkStandalone)

		return () => {
			mediaQuery.removeEventListener("change", checkStandalone)
			document.body.classList.remove("pwa-standalone")
		}
	}, [])

	useEffect(() => {
		// Track navigation to enable/disable back button
		// Can go back if not on home page and history has entries
		setCanGoBack(location.pathname !== "/" && history.length > 1)
	}, [location.pathname, history.length])

	// Don't render if not in standalone mode
	if (!isStandalone) return null

	const isHome = location.pathname === "/"
	const isMessages = location.pathname.startsWith("/messages")
	const isDashboard = location.pathname === "/dashboard"
	const isAccount = location.pathname === "/account/edit"

	const handleBack = () => {
		if (canGoBack) {
			history.goBack()
		}
	}

	const handleForward = () => {
		history.goForward()
	}

	const handleRefresh = () => {
		window.location.reload()
	}

	const handleHome = () => {
		if (!isHome) {
			history.push("/")
		}
	}

	const handleMessages = () => {
		if (!isMessages) {
			history.push("/messages")
		}
	}

	const handleDashboard = () => {
		if (!isDashboard) {
			history.push("/dashboard")
		}
	}

	const handleAccount = () => {
		if (!isAccount) {
			history.push("/account/edit")
		}
	}

	return (
		<nav className="PWANavBar" role="navigation" aria-label="App navigation">
			{/* Back button */}
			<button
				type="button"
				className={`nav-btn ${!canGoBack ? "disabled" : ""}`}
				onClick={handleBack}
				disabled={!canGoBack}
				aria-label="Go back"
				title="Back"
			>
				<FontAwesomeIcon icon={faChevronLeft} />
			</button>

			{/* Forward button */}
			<button type="button" className="nav-btn" onClick={handleForward} aria-label="Go forward" title="Forward">
				<FontAwesomeIcon icon={faChevronRight} />
			</button>

			{/* Refresh button (moved before Home) */}
			<button type="button" className="nav-btn" onClick={handleRefresh} aria-label="Refresh page" title="Refresh">
				<FontAwesomeIcon icon={faRedo} />
			</button>

			{/* Home button (centered) */}
			<button
				type="button"
				className={`nav-btn home-btn ${isHome ? "active" : ""}`}
				onClick={handleHome}
				aria-label="Go to courses"
				title="Home"
			>
				<FontAwesomeIcon icon={faHome} />
			</button>

			{/* Messages button (new) */}
			<button
				type="button"
				className={`nav-btn ${isMessages ? "active" : ""}`}
				onClick={handleMessages}
				aria-label="Messages"
				title="Messages"
			>
				<FontAwesomeIcon icon={faEnvelope} />
				{unreadCount > 0 && <span className="nav-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
			</button>

			{/* Dashboard button */}
			<button
				type="button"
				className={`nav-btn ${isDashboard ? "active" : ""}`}
				onClick={handleDashboard}
				aria-label="Dashboard"
				title="Dashboard"
			>
				<FontAwesomeIcon icon={faChartBar} />
			</button>

			{/* Account button */}
			<button
				type="button"
				className={`nav-btn ${isAccount ? "active" : ""}`}
				onClick={handleAccount}
				aria-label="My account"
				title="Account"
			>
				<FontAwesomeIcon icon={faUser} />
			</button>
		</nav>
	)
}

export default connect(state => ({
	unreadCount: state.messageReducer.unreadCount,
}))(PWANavBar)
